/**
 * Subcomponent Editor Modal
 * Requirements: 9.1, 9.2, 9.3, 9.4
 */
import { useState, useEffect, useRef } from 'react';
import { X, Loader2, Save, Eye, Code } from 'lucide-react';
import DOMPurify from 'dompurify';
import type { SubcomponentType } from '../types';
import { SUBCOMPONENT_LABELS_LONG } from '../constants';
import { useEscapeClose } from '../hooks/useEscapeClose';
import { useFocusTrap } from '../hooks/useFocusTrap';

/**
 * Detect whether content is YAML (not HTML)
 */
function isYamlContent(content: string): boolean {
  const trimmed = content.trim();
  // YAML content won't start with HTML tags and will have key: value patterns
  if (trimmed.startsWith('<')) return false;
  // Check for YAML-like patterns: "key:" at start of line
  return /^[a-zA-Z_][a-zA-Z0-9_]*\s*:/m.test(trimmed);
}

/**
 * Format HTML string with proper indentation for readability.
 * Skips formatting for YAML content to preserve whitespace structure.
 */
function formatHtml(html: string): string {
  if (!html || !html.trim()) return html;

  // Don't format YAML content - newlines and indentation are structural
  if (isYamlContent(html)) return html;

  // Self-closing tags that don't need closing tags
  const selfClosingTags = new Set(['br', 'hr', 'img', 'input', 'meta', 'link', 'area', 'base', 'col', 'embed', 'source', 'track', 'wbr']);

  // Inline tags that shouldn't force newlines
  const inlineTags = new Set(['a', 'span', 'strong', 'em', 'b', 'i', 'u', 'small', 'mark', 'sub', 'sup', 'code']);

  let formatted = '';
  let indent = 0;
  const indentStr = '  '; // 2 spaces per level

  // First, normalize the HTML by removing existing formatting
  const normalized = html
    .replace(/>\s+</g, '><')  // Remove whitespace between tags
    .replace(/\s+/g, ' ')      // Normalize whitespace
    .trim();

  // Split into tokens (tags and text content)
  const tokens = normalized.match(/<[^>]+>|[^<]+/g) || [];

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i].trim();
    if (!token) continue;

    if (token.startsWith('</')) {
      // Closing tag - decrease indent first
      indent = Math.max(0, indent - 1);
      const tagName = token.match(/<\/(\w+)/)?.[1]?.toLowerCase() || '';

      // Check if previous content was on same line (inline content)
      if (formatted.endsWith('>') || inlineTags.has(tagName)) {
        formatted += token;
      } else {
        formatted += '\n' + indentStr.repeat(indent) + token;
      }
    } else if (token.startsWith('<')) {
      // Opening tag or self-closing
      const tagMatch = token.match(/<(\w+)/);
      const tagName = tagMatch?.[1]?.toLowerCase() || '';
      const isSelfClosing = selfClosingTags.has(tagName) || token.endsWith('/>');
      const isInline = inlineTags.has(tagName);

      // Add newline before tag (unless it's inline or first token)
      if (formatted && !isInline) {
        formatted += '\n' + indentStr.repeat(indent);
      } else if (formatted && isInline) {
        // Inline tags don't get newlines
      } else if (formatted) {
        formatted += '\n' + indentStr.repeat(indent);
      }

      formatted += token;

      // Increase indent for opening tags (not self-closing, not inline)
      if (!isSelfClosing && !isInline) {
        indent++;
      }
    } else {
      // Text content
      const text = token.trim();
      if (text) {
        formatted += text;
      }
    }
  }

  return formatted.trim();
}

interface SubcomponentEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (content: string) => Promise<void>;
  jobid: string;
  component: SubcomponentType;
  initialContent: string;
}

export function SubcomponentEditor({
  isOpen,
  onClose,
  onSave,
  component,
  initialContent,
}: SubcomponentEditorProps) {
  const [content, setContent] = useState(initialContent);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [viewMode, setViewMode] = useState<'render' | 'edit'>('render');
  const hasFormattedRef = useRef(false);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setContent(initialContent);
    setHasChanges(false);
    setViewMode('render'); // Start in render mode
    hasFormattedRef.current = false; // Reset formatting flag when content changes
  }, [initialContent, isOpen]);

  // Format HTML when switching to edit mode for the first time
  useEffect(() => {
    if (viewMode === 'edit' && !hasFormattedRef.current && content) {
      const formatted = formatHtml(content);
      if (formatted !== content) {
        setContent(formatted);
      }
      hasFormattedRef.current = true;
    }
  }, [viewMode, content]);

  useEscapeClose(handleCancel, isOpen);
  useFocusTrap(modalRef, isOpen);

  if (!isOpen) return null;

  async function handleSave() {
    setSaving(true);
    try {
      await onSave(content);
      setHasChanges(false);
      onClose();
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    if (hasChanges) {
      if (confirm('You have unsaved changes. Are you sure you want to close?')) {
        onClose();
      }
    } else {
      onClose();
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-label="Edit Subcomponent">
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Edit {SUBCOMPONENT_LABELS_LONG[component]}</h2>
          <div className="flex items-center space-x-2">
            {/* Toggle between Render and Edit modes */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('render')}
                className={`px-3 py-1.5 rounded flex items-center text-sm font-medium transition-colors ${
                  viewMode === 'render'
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Eye className="w-4 h-4 mr-1.5" />
                Render
              </button>
              <button
                onClick={() => setViewMode('edit')}
                className={`px-3 py-1.5 rounded flex items-center text-sm font-medium transition-colors ${
                  viewMode === 'edit'
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Code className="w-4 h-4 mr-1.5" />
                Edit
              </button>
            </div>
            <button onClick={handleCancel} className="p-1 hover:bg-gray-100 rounded" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 p-4 overflow-auto min-h-0">
          {viewMode === 'render' ? (
            <div className="h-full border border-gray-200 rounded-lg p-6 bg-white overflow-auto">
              {isYamlContent(content || '') ? (
                <pre className="whitespace-pre-wrap font-mono text-sm text-gray-800 leading-relaxed">
                  {content || 'No content yet'}
                </pre>
              ) : (
                <>
                  <style>{`
                    /* Resume base styles */
                    .contact h1 { font-size: 2rem; font-weight: bold; margin-bottom: 0.5rem; }
                    .contact address { font-style: normal; display: flex; flex-wrap: wrap; gap: 1rem; margin-top: 0.5rem; }
                    .contact a { color: #2563eb; text-decoration: none; }
                    .contact a:hover { text-decoration: underline; }
                    .contact .phone { color: #4b5563; }

                    .summary p { line-height: 1.6; color: #374151; margin-bottom: 1rem; }

                    .skills h2, .highlights h2, .experience h2, .education h2, .awards h2, .keynotes h2 {
                      font-size: 1.5rem;
                      font-weight: bold;
                      margin-bottom: 1rem;
                      border-bottom: 2px solid #e5e7eb;
                      padding-bottom: 0.5rem;
                    }

                    .skills ul, .highlights ul { list-style-type: disc; margin-left: 1.5rem; }
                    .skills li, .highlights li { margin-bottom: 0.5rem; line-height: 1.5; }

                    .experience .job, .education .degree, .awards .award, .keynotes .keynote {
                      margin-bottom: 1.5rem;
                      padding-bottom: 1.5rem;
                      border-bottom: 1px solid #e5e7eb;
                    }
                    .experience .job:last-child, .education .degree:last-child,
                    .awards .award:last-child, .keynotes .keynote:last-child {
                      border-bottom: none;
                    }

                    .experience h3, .education h3, .awards h3, .keynotes h3 {
                      font-size: 1.25rem;
                      font-weight: 600;
                      margin-bottom: 0.5rem;
                    }

                    .company, .institution, .issuer, .event {
                      font-size: 1rem;
                      color: #4b5563;
                      margin-bottom: 0.25rem;
                    }

                    .dates, .graduation, .date, .location {
                      font-size: 0.875rem;
                      color: #6b7280;
                      margin-bottom: 0.5rem;
                    }

                    .description, .gpa {
                      color: #374151;
                      line-height: 1.6;
                      margin-bottom: 0.75rem;
                    }

                    .achievements {
                      list-style-type: disc;
                      margin-left: 1.5rem;
                      margin-top: 0.5rem;
                    }
                    .achievements li {
                      margin-bottom: 0.5rem;
                      line-height: 1.5;
                    }

                    .cover-letter p {
                      line-height: 1.6;
                      margin-bottom: 1rem;
                    }
                  `}</style>
                  <div
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content || '<p class="text-gray-500 italic">No content yet</p>') }}
                  />
                </>
              )}
            </div>
          ) : (
            <div className="h-full flex flex-col">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isYamlContent(content || '') ? 'YAML Content' : 'HTML Content'}
              </label>
              <textarea
                value={content}
                onChange={(e) => {
                  setContent(e.target.value);
                  setHasChanges(true);
                }}
                className="flex-1 w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                placeholder="Enter HTML content..."
                style={{ minHeight: '500px' }}
              />
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-4 border-t bg-gray-50">
          <div className="text-sm text-gray-500">
            {hasChanges && <span className="text-amber-600">Unsaved changes</span>}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center"
            >
              {saving ? (
                <span role="status" aria-label="Saving">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                </span>
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {/* Cover letter saves also re-render the PDF (~10s) and open it
                  in a new tab — say so, or the long spinner reads as a hang. */}
              {saving && component === 'coverletter' ? 'Saving & rebuilding PDF…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
